import { useTheme } from 'styled-components';
import DoghnutChartWidget from '../../components/common/DoghnutChartWidget/DoghnutChartWidget';
import { FilterConfig } from '../../components/common/DropdownFilter/types';
import ItemsList from '../../components/common/ItemsList/ItemsList';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import { ThemeType } from '../../theme/themeType';
import S from './AlertsPageStyles';
import AlertsItem from './AlertsItem/AlertsItem';
import useGetAlertRisks from '../../API/hooks/alerts/useGetAlertsRisks';
import useGetAlerts from '../../API/hooks/alerts/useGetAlerts';
import useGetAlertTypes from '../../API/hooks/alerts/useGetAlertsTypes';
import useGetAlertsHeatmap from '../../API/hooks/alerts/useGetAlertsHeatmap';
import MapWidget from '../../components/common/Map/Map';
import {
  AlertRisksDognutConfig,
  AlertRisksKeys,
  AlertTypesDognutConfig,
  AlertTypesKeys,
} from '../../API/types/Alerts';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import {
  getStartOfMonth, getEndOfTheDay, getTimeRangeText, getPeriodDisplayLabel,
} from '../../utils/dateUtils';
import { getFilterFields, queryFilterByGroups } from '../../utils/queryFilters';
import { getLocalStorageKey } from '../../utils/storageUtils';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetAlertsReport from '../../API/hooks/reports/useGetAlertsReport';
import Icon from '../../components/common/Icon/Icon';
import TimeSelection from '../../components/common/Toolbar/TimeSelection';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import { downloadFile, createCSV } from '../../utils/commonUtils';
import useGetCurrentPage from '../../API/hooks/useGetCurrentPage';
import { alertsPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import useGetAlertsWithInvalid from '../../API/hooks/alerts/useGetAlertsWithInvalid';

const SEARCH_CONFIG = [
  {
    type: 'sensor',
    options: [
      { IMU: 'IMU' },
      { frontCamera: 'Front Camera' },
      { inwardCamera: 'Inward Camera' },
      { cloud: 'Cloud' },
    ],
  },
  {
    type: 'name',
    options: [
      { DevStreamEventFcw: 'Forward collision warning' },
      { DevStreamEventPcw: 'Pedestrian collision warning' },
      { DevStreamEventLdw: 'Lane departure warning' },
      { DevStreamEventOverspeed: 'Over speeding warning' },
      { DevStreamEventWireCut: 'Power Supply Cut Warning' },
      { DevStreamEventDrowsiness: 'Drowsiness warning' },
      { DevStreamEventEyesOffRoad: 'Eyes off road' },
      { DevStreamEventCameraBlockageInterior: 'The interior camera field or view is blocked' },
      { DevStreamEventCameraBlockageExterior: 'The exterior camera field or view is blocked' },
      { DevStreamEventMobilePhoneUsage: 'Mobile phone usage' },
      { DevStreamEventTailgaiting: 'Tailgating warning' },
      { DevStreamEventAccidentCameraRemoval: 'Camera removal warning' },
      { DevStreamEventHighG: 'High G warning' },
      { DevStreamEventHardBrake: 'Hard brake warning' },
      { DevStreamEventHardAcceleration: 'Hard acceleration warning' },
      { DevStreamEventHardTurn: 'Hard turn warning' },
      { DevStreamDriverNotFound: 'Driver identification issue' },
      { DrivingOvertime: 'Driver reached the limit of driving hours' },
      { DriverUnauthorized: 'Unauthorized driver detected' },
      { DriverUnknown: 'Unknown driver detected' },
      // TODO unblock when backend is ready
      // { DevStreamUnauthorizedDriver: 'Unauthorized driver detected' },
    ],
  },
  {
    type: 'riskLevel',
    options: [
      { urgent: 'Urgent Risk' },
      { medium: 'Medium Risk' },
      { low: 'Low Risk' },
    ],
  },
  {
    type: 'isThumbnail',
    display: 'radio',
    options: [
      { null: 'All' },
      { true: 'Alerts with videos' },
      { false: 'Alerts without videos' },
    ],
  },
  {
    type: 'isValid',
    display: 'radio',
    options: [
      { null: 'All' },
      { true: 'Only valid' },
      { false: 'Only invalid' },
    ],
  },
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { eventDate_DESC: 'Newest first' },
      { eventDate_ASC: 'Oldest first' },
    ],
  },
] as FilterConfig[];

function AlertsPage(): JSX.Element {
  const theme = useTheme() as ThemeType;

  const alertTypesChartConfig: AlertTypesDognutConfig = {
    cards: {
      IMU: {
        color: theme.charts.alertImu,
        label: 'IMU',
      },
      frontCamera: {
        color: theme.charts.alertFrontCamera,
        label: 'Front cam',
      },
      inwardCamera: {
        color: theme.charts.alertInwardCamera,
        label: 'Inw cam',
      },
      cloud: {
        color: theme.charts.alertCloud,
        label: 'Cloud',
      },
    },
    title: 'Alert types',
  };

  const alertRisksChartConfig: AlertRisksDognutConfig = {
    cards: {
      urgent: {
        color: theme.charts.alertRiskUrgent,
        label: 'Urgent',
      },
      medium: {
        color: theme.charts.alertRiskMedium,
        label: 'Medium',
      },
      low: {
        color: theme.charts.alertRiskLow,
        label: 'Low',
      },
    },
    title: 'Alert risks',
  };

  const fleetId = useGetFleetId();
  const [groupFilters, setGroupFilters] = useGroupFilters();
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('alerts-page-filters'), alertsPageFilters);
  const { data: fleetData } = useGetFleet(fleetId);

  const { data: alertsHeatmap } = useGetAlertsHeatmap(
    fleetId,
    `?from=${(filters || alertsPageFilters)?.startDate?.[0]}&to=${(filters || alertsPageFilters)?.endDate?.[0] || getEndOfTheDay().toISOString()}`,
  );

  const { startDate, endDate } = filters || alertsPageFilters;

  const searchInputQuery = filters?.input?.[0] ? `&input=${encodeURIComponent(filters.input[0])}` : '';
  const { data: risksChartData, isLoading: risksChartDataisLoading } = useGetAlertRisks(fleetId, `?limit=1000&skip=0${getFilterFields({ startDate, endDate }, [], 'eventDate')}${queryFilterByGroups(groupFilters)}${searchInputQuery}`);
  const { data: typesChartData, isLoading: typesChartDataisLoading } = useGetAlertTypes(fleetId, `?limit=1000&skip=0${getFilterFields({ startDate, endDate }, [], 'eventDate')}${queryFilterByGroups(groupFilters)}${searchInputQuery}`);

  Object.keys(alertRisksChartConfig.cards).forEach((key: string): void => {
    alertRisksChartConfig.cards[key as AlertRisksKeys].count = risksChartData?.[key as AlertRisksKeys] || 0;
  });

  Object.keys(alertTypesChartConfig.cards).forEach((key: string): void => {
    alertTypesChartConfig.cards[key as AlertTypesKeys].count = typesChartData?.[key as AlertTypesKeys] || 0;
  });

  const periodLabel = getPeriodDisplayLabel(filters);
  alertTypesChartConfig.title = `Alert types • ${periodLabel}`;
  alertRisksChartConfig.title = `Alert risks • ${periodLabel}`;

  const reportName = `${fleetData?.name || 'fleet_na'}_alerts_${getTimeRangeText(filters)}`;
  return (
    <S.Wrapper>
      <div className="header">
        <Toolbar title="Alerts and tickets" />
        <div className="wrapper">
          <TimeSelection allTimeOption filters={filters} setFilters={setFilters} />
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
      </div>
      <div className="widgets-container">
        <MapWidget fleetId={fleetId} heatMap={alertsHeatmap} groupFilters={groupFilters || {}} />
        <div className="statistics-container">
          <button
            className="report-btn"
            title="Create report"
            type="button"
            onClick={() => {
              downloadFile(createCSV([{
                ...risksChartData, ...typesChartData,
              }]), `${reportName}_widgets`, 'text/csv');
            }}
          >
            <Icon iconKey={IconKeys.SUMMARIZE} />
          </button>
          <DoghnutChartWidget percentage data={alertTypesChartConfig} loading={risksChartDataisLoading} type="quad" />
          <DoghnutChartWidget percentage data={alertRisksChartConfig} loading={typesChartDataisLoading} />
        </div>
      </div>
      {filters?.isValid?.[0] !== 'true' && (
        <p className="notice-invalid-alerts">* The list contains invalid alerts but this doesn't affect the map and statistics above</p>
      )}
      <ItemsList
        entity="alerts"
        title={periodLabel}
        fleetId={fleetId}
        filtersConfig={SEARCH_CONFIG}
        getQuery={useGetAlertsWithInvalid}
        filters={filters}
        defaultFilters={alertsPageFilters}
        groupFilters={groupFilters || {}}
        setFilters={setFilters}
        hasSearch
        hasCreateReport
        reportEndpoint={useGetAlertsReport}
        reportName={reportName}
        curentPage={useGetCurrentPage()}
      >
        <AlertsItem includeVehicleDetails />
      </ItemsList>
    </S.Wrapper>
  );
}

export default AlertsPage;
